import React, { useMemo } from 'react';

import { FormProps } from '@components/admin/FormDialog';
import { LogForm } from '@components/Form';
import { FormAlert } from '@components/Forms/FormAlert';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { AdminInquiryDetail } from '@models/BookingInquiry';
import {
  contactForm,
  contactFormInitialValues,
  ContactType,
  createValidateContactForm,
} from '@models/Contact';
import { useCommonStyles } from '@styles';
import { createValidate } from '@utils/forms';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { isEqual } from 'lodash-es';

export interface BookingInquiryContactFormProps extends FormProps {
  contactType: Exclude<ContactType, 'pickup_address'>;
  adminInquiryDetail: AdminInquiryDetail;
}

export const BookingInquiryContactForm: React.FC<BookingInquiryContactFormProps> = (props) => {
  const { formikRef, contactType, adminInquiryDetail } = props;

  const havePreferredArrivalDate = contactType === 'receiver';

  const classes = useCommonStyles();

  const contactMutation = usePatchBookingInquiry(adminInquiryDetail.d2d_no);

  const initialValues = useMemo(
    () => ({
      [contactType]: {
        ...(adminInquiryDetail[contactType] ?? {
          ...contactFormInitialValues,
        }),
      },
      preferred_arrival_date: adminInquiryDetail.preferred_arrival_date,
    }),
    [adminInquiryDetail],
  );

  return (
    <Formik<typeof initialValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={createValidate(
        t.type({
          [contactType]: contactForm,
        }),
        {
          fields: {
            [contactType]: createValidateContactForm(contactType),
          },
        },
      )}
      onSubmit={async (values) => {
        if (
          !isEqual(values[contactType], initialValues[contactType]) ||
          values.preferred_arrival_date !== initialValues.preferred_arrival_date
        ) {
          await contactMutation.mutateAsync(values);
        }
      }}
    >
      <Form>
        <SubmittingOverlay />
        <LogForm name={contactType} disabled={true} />
        <FormAlert mutation={contactMutation} gutter />
        <div
          className={clsx(classes.normalText, classes.fontWeight600Important)}
          style={{ paddingBottom: '1rem' }}
        >
          {bookingInquiryStrings[contactType]}
        </div>
        <ContactForm name={contactType} contactType={contactType} zipcodeRequired />
        {havePreferredArrivalDate && (
          <ManagementTextInput
            name="preferred_arrival_date"
            label={bookingInquiryStrings.preferredArrivalDate}
          />
        )}
      </Form>
    </Formik>
  );
};
